import React, { useCallback, useEffect, useRef, useState } from "react";
import { customerSupportApi } from "./customerSupportApi";

/* ── Product filter ──
   FixDesk mirrors support tickets from two products, eMedHub and Ayurveda, and
   each team works its own queue. The Dashboard and the Tickets page share one
   Product choice, eMedHub by default. It is remembered in this browser so it
   carries across both pages and reloads.
   The options come from the backend (/customer-support/tenants/ → products),
   which always lists eMedHub and Ayurveda; the same two are shown until it
   answers. */

const STORAGE_KEY = "csProduct";
export const DEFAULT_PRODUCT = "emedhub";
const FALLBACK_PRODUCTS = [
    { code: "emedhub", label: "eMedHub" },
    { code: "ayurveda", label: "Ayurveda" },
];

const readStored = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) || DEFAULT_PRODUCT;
    } catch {
        return DEFAULT_PRODUCT;
    }
};

export const useCsProduct = () => {
    const [product, setProductState] = useState(readStored);
    const [products, setProducts] = useState(FALLBACK_PRODUCTS);

    const setProduct = useCallback((value) => {
        const next = value || DEFAULT_PRODUCT;
        setProductState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            /* storage blocked: the choice just isn't remembered */
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        customerSupportApi.getTenants()
            .then((r) => {
                if (!cancelled && Array.isArray(r.products) && r.products.length) setProducts(r.products);
            })
            .catch(() => { /* keep the two built-in products */ });
        return () => { cancelled = true; };
    }, []);

    // A remembered product that no longer exists falls back to eMedHub.
    useEffect(() => {
        if (!products.some((p) => p.code === product)) setProduct(DEFAULT_PRODUCT);
    }, [product, products, setProduct]);

    const productLabel = (products.find((p) => p.code === product) || {}).label || "eMedHub";
    // The Sync button always pulls every product: "eMedHub & Ayurveda".
    const allProductsLabel = products.map((p) => p.label).join(" & ");
    return { product, setProduct, products, productLabel, allProductsLabel };
};

export const ProductPicker = ({ product, products, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [open]);

    const current = products.find((p) => p.code === product) || products[0] || {};

    return (
        <div className="filter-group" ref={ref}>
            <button
                type="button"
                className="filter-select active"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                title="Show one product's tickets"
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: .8 }}>
                    <polygon points="12 2 2 7 12 12 22 7 12 2" />
                    <polyline points="2 17 12 22 22 17" />
                    <polyline points="2 12 12 17 22 12" />
                </svg>
                <span className="lbl-dim">Product:</span>{" "}
                <span className="val">{current.label}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open && (
                <div className="dropdown show" role="listbox" aria-label="Product">
                    {products.map((p) => {
                        const selected = p.code === product;
                        return (
                            <div
                                key={p.code}
                                role="option"
                                aria-selected={selected}
                                className={`dd-option ${selected ? "selected" : ""}`}
                                onClick={() => { onChange(p.code); setOpen(false); }}
                            >
                                <div className="dd-check">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                                <span className="dd-label">{p.label}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
